import { inject as service } from '@ember/service';
import { assert } from '@ember/debug';
import { variation } from 'ember-launch-darkly';
import { macroCondition, isDevelopingApp } from '@embroider/macros';

import PaginatedRoute from 'core/routes/paginated-route';

export default class CloudOrgsDetailProjectsListRoute extends PaginatedRoute {
  @service abilities;
  @service api;
  @service billing;
  @service currentUser;
  @service lastAccessedProject;
  @service('resources.project') projects;
  @service router;
  @service session;
  @service userContext;

  async model({ pageSize = 10, nextPageToken, previousPageToken }) {
    const { organization } = this.userContext;
    const currentPageSize = pageSize ? Number(pageSize) : pageSize;

    await this.userContext.setProject(null);

    const { projects, pagination } =
      await this.api.resourceManager.project.projectServiceList(
        'ORGANIZATION',
        organization.id,
        currentPageSize,
        nextPageToken,
        previousPageToken
      );

    const metaLastOpenedProject = await this.lastAccessedProject.getProject(
      organization.id
    );

    let lastOpenedProject;
    let resourcePermissions = {};

    try {
      ({ resourcePermissions } =
        await this.api.resourceManager.authorization.authorizationServiceBatchTestIamPermissions(
          {
            resources: projects.map((project) => {
              return {
                permissions: [
                  'resource-manager.projects.create',
                  'resource-manager.projects.delete',
                  'resource-manager.projects.get',
                  'resource-manager.projects.get-iam-policy',
                  'resource-manager.projects.list',
                  'resource-manager.projects.set-iam-policy',
                  'resource-manager.projects.set-system-managed-iam-policy',
                  'resource-manager.projects.update',
                ],
                resourceName: `project/${project.id}`,
              };
            }),
          }
        ));
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
    }

    if (metaLastOpenedProject?.id) {
      lastOpenedProject = projects.find((project) => {
        return project.id === metaLastOpenedProject.id;
      });

      if (!lastOpenedProject) {
        try {
          const { project } =
            await this.api.resourceManager.project.projectServiceGet(
              metaLastOpenedProject.id
            );
          lastOpenedProject = project;
        } catch (e) {
          assert(
            `Unable to fetch last opened project ${metaLastOpenedProject.id}. Error:`,
            e
          );
        }
      }
    }

    return {
      currentPageSize,
      lastOpenedProject,
      organization,
      pagination,
      projects,
      resourcePermissions,
    };
  }

  async redirect({ organization, projects = [] }) {
    if (
      projects.length === 0 &&
      this.abilities.can('create project', projects) &&
      variation('hcp-ui-project-list-auto-create-enabled')
    ) {
      const project = await this.projects.createProject.perform(
        this.currentUser.defaultProjectName,
        '',
        organization
      );

      this.router.transitionTo('cloud.orgs.detail.projects.detail', project.id);
    }
  }

  async afterModel({ projects = [] }) {
    if (variation('hcp-ui-project-list-auto-create-enabled')) {
      if (!projects.length) {
        this.router.transitionTo('cloud.orgs.detail');
      }
    }
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      controller.resetPagination();
    }
  }
}
